import { useEffect, useRef, useCallback } from "react";
import { useThree } from "@react-three/fiber";
import * as THREE from "three";
import {
  useMeshes,
  useSelectedModuleRootLink,
  useInstances,
  useHoverConnectionTarget,
  useViewerStore,
} from "../store";
import { useWebSocket } from "../WebSocket";
import { recordMove, recordConnect } from "../hooks";

// Position range limit (4m x 4m = -2 to 2)
const POSITION_LIMIT = 2;

interface DragState {
  isDragging: boolean;
  instanceId: string | null;
  offset: THREE.Vector3;
  // The pointer that started the drag. A second finger landing mid-drag
  // reports a different id and must not steer the module.
  pointerId: number | null;
}

export function DragHandler({
  children,
  onDragStateChange,
  disabled,
}: {
  children: React.ReactNode;
  onDragStateChange: (isDragging: boolean) => void;
  disabled?: boolean;
}) {
  const { camera, gl, raycaster } = useThree();
  const meshes = useMeshes();
  const rootLink = useSelectedModuleRootLink();
  const instances = useInstances();
  const hoverConnectionTarget = useHoverConnectionTarget();
  const {
    sendUpdateInstancePosition,
    sendTranslationEnd,
    sendSelectInstance,
    sendConnectModules,
  } = useWebSocket();
  const setSelectedMesh = useViewerStore((state) => state.setSelectedMesh);
  const setDragging = useViewerStore((state) => state.setDragging);
  const setDragOffset = useViewerStore((state) => state.setDragOffset);

  const dragState = useRef<DragState>({
    isDragging: false,
    instanceId: null,
    offset: new THREE.Vector3(),
    pointerId: null,
  });

  // The position of the instance at the start of the drag
  const dragStartInstancePos = useRef<[number, number, number]>([0, 0, 0]);

  // Store refs to avoid stale closures
  const rootLinkRef = useRef(rootLink);
  const instancesRef = useRef(instances);
  const hoverConnectionTargetRef = useRef(hoverConnectionTarget);
  useEffect(() => {
    rootLinkRef.current = rootLink;
  }, [rootLink]);
  useEffect(() => {
    instancesRef.current = instances;
  }, [instances]);
  useEffect(() => {
    hoverConnectionTargetRef.current = hoverConnectionTarget;
  }, [hoverConnectionTarget]);

  // Z-up: ground plane is XY at Z=0
  const groundPlane = useRef(new THREE.Plane(new THREE.Vector3(0, 0, 1), 0));

  // Check if point is inside canvas
  const isInsideCanvas = useCallback(
    (clientX: number, clientY: number): boolean => {
      const rect = gl.domElement.getBoundingClientRect();
      return (
        clientX >= rect.left &&
        clientX <= rect.right &&
        clientY >= rect.top &&
        clientY <= rect.bottom
      );
    },
    [gl]
  );

  const getPlaneIntersection = useCallback(
    (clientX: number, clientY: number): THREE.Vector3 | null => {
      const rect = gl.domElement.getBoundingClientRect();
      const x = ((clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((clientY - rect.top) / rect.height) * 2 + 1;

      raycaster.setFromCamera(new THREE.Vector2(x, y), camera);

      const intersection = new THREE.Vector3();
      const result = raycaster.ray.intersectPlane(groundPlane.current, intersection);

      return result ? intersection : null;
    },
    [camera, gl, raycaster]
  );

  const handlePointerMove = useCallback(
    (event: PointerEvent) => {
      if (!dragState.current.isDragging || !dragState.current.instanceId) return;
      if (event.pointerId !== dragState.current.pointerId) return;

      // If mouse is outside canvas, do nothing (keep last position)
      if (!isInsideCanvas(event.clientX, event.clientY)) {
        return;
      }

      const intersection = getPlaneIntersection(event.clientX, event.clientY);
      if (!intersection) return;

      // Apply offset
      intersection.sub(dragState.current.offset);

      // Clamp to position limits (4m x 4m range)
      const clampedX = Math.max(-POSITION_LIMIT, Math.min(POSITION_LIMIT, intersection.x));
      const clampedY = Math.max(-POSITION_LIMIT, Math.min(POSITION_LIMIT, intersection.y));

      // Apply local offset instantly (no server round-trip).
      // All instances in the drag group (parent + descendants) share this offset.
      const startPos = dragStartInstancePos.current;
      setDragOffset([
        clampedX - startPos[0],
        clampedY - startPos[1],
        0,
      ]);
    },
    [
      getPlaneIntersection,
      isInsideCanvas,
      setDragOffset,
    ]
  );

  const handlePointerUp = useCallback((event: PointerEvent) => {
    if (dragState.current.isDragging) {
      if (event.pointerId !== dragState.current.pointerId) return;
      const instanceId = dragState.current.instanceId;

      // Compute the final absolute position from start + current offset
      const store = useViewerStore.getState();
      const offset = store.dragOffset;
      const startPos = dragStartInstancePos.current;
      const finalPosition: [number, number, number] = [
        startPos[0] + offset[0],
        startPos[1] + offset[1],
        startPos[2] + offset[2],
      ];

      // Clear drag state in store (bakes offset into mesh positions)
      setDragging(null);

      // Send final position to server
      if (instanceId) {
        sendUpdateInstancePosition(instanceId, finalPosition);
      }

      // Check if we should connect (drag-to-connect via hover target)
      const target = hoverConnectionTargetRef.current;
      if (target && instanceId) {
        // Use the DRAGGED instance as the child, not the tree root.
        // This allows reconnecting a module that's already part of a tree.
        // The backend will disconnect it from its current parent and reconnect.
        const childLinkName = rootLinkRef.current?.name ?? "base_link";

        console.log("[DragHandler] Connecting on drop (using hover target):", {
          childInstanceId: instanceId,
          childLinkName,
          parentInstanceId: target.parentInstanceId,
          parentLinkName: target.parentLinkName,
        });

        // Connect the modules (undo = disconnect, instant)
        recordConnect(instanceId);
        sendConnectModules(
          instanceId,
          childLinkName,
          target.parentInstanceId,
          target.parentLinkName
        );
      }

      if (gl.domElement.hasPointerCapture(event.pointerId)) {
        gl.domElement.releasePointerCapture(event.pointerId);
      }
      dragState.current.isDragging = false;
      dragState.current.instanceId = null;
      dragState.current.pointerId = null;
      onDragStateChange(false);
      document.body.style.cursor = "default";

      // Notify backend that translation ended (to refresh IK gizmos)
      sendTranslationEnd();

      // Remove document-level listeners
      document.removeEventListener("pointermove", handlePointerMove);
      document.removeEventListener("pointerup", handlePointerUp);
      document.removeEventListener("pointercancel", handlePointerUp);
    }
  }, [
    gl,
    onDragStateChange,
    handlePointerMove,
    sendTranslationEnd,
    sendConnectModules,
    sendUpdateInstancePosition,
    setDragging,
  ]);

  // Start drag from a specific instance
  const startDrag = useCallback(
    (instanceId: string, meshUuid: string, event: PointerEvent) => {
      // Don't start drag if disabled (e.g., when end coords gizmo is active)
      if (disabled) return;

      // A drag already owns a pointer: a second finger must not hijack it.
      if (dragState.current.isDragging) return;

      const intersection = getPlaneIntersection(event.clientX, event.clientY);
      if (!intersection) return;

      // Get the current position of the instance from a mesh
      const meshData = meshes[meshUuid];
      if (!meshData) return;

      // Calculate offset from click point to mesh position
      const meshPos = new THREE.Vector3(...meshData.position);
      dragState.current.offset.copy(intersection).sub(meshPos);
      dragState.current.offset.z = 0; // Keep Z offset at 0

      dragState.current.isDragging = true;
      dragState.current.instanceId = instanceId;
      dragState.current.pointerId = event.pointerId;
      // Route every later event for this pointer to the canvas even once the
      // finger leaves it, so a drag that runs off the edge still ends cleanly.
      try {
        gl.domElement.setPointerCapture(event.pointerId);
      } catch {
        // The pointer is already gone (fast tap); the listeners below still
        // finish the drag.
      }
      onDragStateChange(true);
      document.body.style.cursor = "move";

      // Record the instance position for undo
      const inst = instancesRef.current.find((i) => i.instance_id === instanceId);
      if (inst) {
        recordMove(instanceId, inst.position);
      }

      // Use the clicked mesh's position as the drag reference point.
      // dragState.offset is computed relative to meshPos, so handlePointerMove
      // produces target = meshPos + mouseDelta. We must use the same meshPos
      // as the start so that dragOffset starts at exactly [0,0,0].
      dragStartInstancePos.current = [...meshData.position];

      // Tell the store which instance is being dragged (offset starts at 0)
      setDragging(instanceId);

      // Add document-level listeners to track the pointer even outside canvas.
      // pointercancel matters on touch: the OS can revoke a touch mid-drag
      // (system gesture, call), and without it the module stays stuck to the
      // finger with no way to drop it.
      document.addEventListener("pointermove", handlePointerMove);
      document.addEventListener("pointerup", handlePointerUp);
      document.addEventListener("pointercancel", handlePointerUp);

      // Select the instance
      setSelectedMesh(meshUuid);
      sendSelectInstance(instanceId);
    },
    [disabled, gl, getPlaneIntersection, meshes, onDragStateChange, setSelectedMesh, sendSelectInstance, handlePointerMove, handlePointerUp, setDragging]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      document.removeEventListener("pointermove", handlePointerMove);
      document.removeEventListener("pointerup", handlePointerUp);
      document.removeEventListener("pointercancel", handlePointerUp);
    };
  }, [handlePointerMove, handlePointerUp]);

  // Expose startDrag function via window for SceneMesh to use
  useEffect(() => {
    (window as { __startDrag?: typeof startDrag }).__startDrag = startDrag;
    return () => {
      delete (window as { __startDrag?: typeof startDrag }).__startDrag;
    };
  }, [startDrag]);

  return <>{children}</>;
}
